import type { ProfileConfig } from "@worker/config-schema";
import { isCoarsePointer, prefersReducedMotion } from "../lib/dom";

/**
 * 3D tilt on cursor position. The card rotates on the X/Y axes following the
 * mouse, up to the configured `maxDeg`. Uses rAF-throttled pointer events so
 * the tilt feels responsive without hammering layout.
 *
 * Disabled when:
 *   - tilt.enabled === false
 *   - touch / coarse pointer device
 *   - user prefers reduced motion AND motion.reducedMotionSuppresses includes "tilt"
 */
export function attachTilt(
  card: HTMLElement,
  cfg: ProfileConfig["card"]["tilt"],
  motion: ProfileConfig["motion"],
): () => void {
  if (!cfg.enabled || isCoarsePointer()) return () => {};
  if (
    cfg.respectReducedMotion &&
    motion.respectReducedMotion &&
    motion.reducedMotionSuppresses.includes("tilt") &&
    prefersReducedMotion()
  ) {
    return () => {};
  }

  const speedMul = motion.speedOverrides["tilt"] ?? 1;
  if (speedMul === 0) return () => {};

  const mode = cfg.mode ?? "3d";

  // Scale the tilt with a quadratic fall-off relative to the global card
  // scale. Chromium's GPU rasterizer blur scales roughly with (rotation *
  // scale), so a linear dampening isn't enough — a card at 2x scale with
  // 4° tilt blurs about as much as a 1x card with 8° tilt. Quadratic
  // keeps blur bounded regardless of scale:
  //   scale 1.0 → full tilt
  //   scale 1.25 → 64% tilt
  //   scale 1.5 → 44% tilt
  //   scale 2.0 → 25% tilt
  // Clamped to 15% minimum so very-large cards still get a hint of tilt.
  // Only relevant in 3D mode — 2D scale+translate doesn't suffer the same
  // texture-resampling blur, so it can run at full strength regardless.
  const globalScale = (() => {
    // Read the viewport-capped effective scale, not the raw inline
    // --global-scale. On mobile the CSS caps the rendered scale at 1
    // even if config sets 1.5; reading the raw value here would
    // over-compensate tilt and make it visually too-flat on phones.
    const v = parseFloat(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--global-scale-effective")
        .trim() || "1",
    );
    return Number.isFinite(v) && v > 0 ? v : 1;
  })();
  const scaleComp =
    mode === "3d"
      ? Math.max(0.15, Math.min(1, 1 / (globalScale * globalScale)))
      : 1;
  const max = cfg.maxDeg * speedMul * scaleComp;

  // 2D mode tuning: a small lift (scale) plus a tiny skew toward the
  // cursor's quadrant reads as "edges leaning back" — same gestalt as
  // 3D rotation but with a 2D affine transform, so no GPU texture blur.
  // Earlier versions translated opposite the cursor, which produced a
  // "gyroball" repulsion feel (hand right → card slides left). Skew
  // gives the edge-tilt impression without the inverse-motion artifact.
  //   skewX → tips the top vs bottom horizontally  (mimics rotateY)
  //   skewY → tips the left vs right vertically    (mimics rotateX)
  // Scaled by speedMul so the motion config still has authority.
  // Max ~3% scale and ~2° skew at the corners — small on purpose; skew
  // distorts content geometry, so we keep it well under what would look
  // visibly warped.
  const liftScale = 1 + 0.03 * speedMul;
  const liftSkewDeg = 2 * speedMul;

  let rafId: number | null = null;
  let pendingX = 0;
  let pendingY = 0;
  let hovering = false;
  // Snapshot of the un-transformed card rect, captured right when the
  // pointer first enters. We hit-test against THIS, not against the card's
  // live getBoundingClientRect — once the card tilts, its post-transform
  // hit area changes and the browser's own pointerenter/pointerleave fire
  // erratically (the corner of the card visually moves away from the
  // cursor → leave fires → we reset → enter fires again → jitter loop).
  // Listening on document with a stable rect breaks the loop entirely.
  let baseRect: DOMRect | null = null;

  const snapshotRect = () => {
    // Read the rect with the transform cleared so it reflects the natural
    // hit area, not the currently-tilted one.
    const prevTransform = card.style.transform;
    card.style.transform = "";
    const r = card.getBoundingClientRect();
    card.style.transform = prevTransform;
    // Inflate (positive) or shrink (negative) by the configured boundary.
    // Builds a fresh DOMRect so width/height stay consistent with the
    // mutated edges — important because we divide by them later.
    const b = cfg.boundaryPx ?? 0;
    baseRect = new DOMRect(
      r.left - b,
      r.top - b,
      Math.max(1, r.width + b * 2),
      Math.max(1, r.height + b * 2),
    );
  };

  const inside = (x: number, y: number, r: DOMRect) =>
    x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

  const onDocMove = (ev: PointerEvent) => {
    if (!baseRect) return;
    const isInside = inside(ev.clientX, ev.clientY, baseRect);

    if (isInside && !hovering) {
      // True enter — refresh snapshot in case page scrolled / layout shifted.
      snapshotRect();
      if (!baseRect) return;
      hovering = true;
      // Shorten the transition while actively tilting so the card tracks
      // the cursor crisply.
      card.style.transition = "transform 80ms linear";
    } else if (!isInside && hovering) {
      // True leave (cursor passed the static rect, not the tilted hit area).
      hovering = false;
      pendingX = 0;
      pendingY = 0;
      card.style.transition = "";
      if (rafId == null) rafId = requestAnimationFrame(apply);
      return;
    }

    if (!hovering || !baseRect) return;
    const nx = ((ev.clientX - baseRect.left) / baseRect.width) * 2 - 1;
    const ny = ((ev.clientY - baseRect.top) / baseRect.height) * 2 - 1;
    pendingX = Math.max(-1, Math.min(1, nx));
    pendingY = Math.max(-1, Math.min(1, ny));
    if (rafId == null) rafId = requestAnimationFrame(apply);
  };

  const onPointerOut = (ev: PointerEvent) => {
    // Pointer left the document entirely (relatedTarget null on pointerout
    // from <html>). Treat as leave so we don't lock the tilt on if the
    // cursor exits the window mid-hover.
    if (ev.relatedTarget) return;
    if (!hovering) return;
    hovering = false;
    pendingX = 0;
    pendingY = 0;
    card.style.transition = "";
    if (rafId == null) rafId = requestAnimationFrame(apply);
  };

  // Re-snapshot on layout-changing events so the hit rect doesn't drift.
  const onLayoutChange = () => {
    if (hovering) snapshotRect();
  };

  function apply() {
    rafId = null;
    if (!hovering && pendingX === 0 && pendingY === 0) {
      // Resting state — clear the transform entirely so the card's base CSS
      // transitions (e.g. during admin live preview) remain unaffected.
      card.style.transform = "";
      // Clear 2D-mode shadow hints too so the resting shadow returns
      // to whatever the .card rule provides.
      card.style.removeProperty("--tilt-shadow-x");
      card.style.removeProperty("--tilt-shadow-y");
      card.style.removeProperty("--tilt-lift");
      return;
    }
    if (mode === "2d") {
      // 2D lift: scale up slightly + skew toward the cursor quadrant.
      // Skew signs match the 3D rotation feel:
      //   pendingY > 0 (cursor lower) → skewY positive → right side dips
      //     (analogous to rotateX(-pendingY*max) tipping the bottom forward)
      //   pendingX > 0 (cursor right) → skewX negative → top leans right
      //     (analogous to rotateY(+pendingX*max) tipping right edge back)
      // No 3D rotation / perspective → no GPU rasterizer blur. The
      // directional shadow still tracks the cursor for depth cues.
      const sx = (-pendingX * liftSkewDeg).toFixed(2);
      const sy = (pendingY * liftSkewDeg).toFixed(2);
      card.style.transform = `scale(${liftScale}) skewX(${sx}deg) skewY(${sy}deg)`;
      card.style.setProperty("--tilt-shadow-x", `${(pendingX * 14).toFixed(1)}px`);
      card.style.setProperty("--tilt-shadow-y", `${(pendingY * 14).toFixed(1)}px`);
      card.style.setProperty("--tilt-lift", "1");
      return;
    }
    const rx = (-pendingY * max).toFixed(2);
    const ry = (pendingX * max).toFixed(2);
    card.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  }

  // Capture the initial rect once mount-time paint settles. rAF + 0ms
  // setTimeout ensures the card has been laid out (admin live-preview
  // re-mounts can fire this before layout completes otherwise).
  requestAnimationFrame(() => snapshotRect());

  document.addEventListener("pointermove", onDocMove, { passive: true });
  document.addEventListener("pointerout", onPointerOut);
  window.addEventListener("scroll", onLayoutChange, { passive: true });
  window.addEventListener("resize", onLayoutChange);

  return () => {
    document.removeEventListener("pointermove", onDocMove);
    document.removeEventListener("pointerout", onPointerOut);
    window.removeEventListener("scroll", onLayoutChange);
    window.removeEventListener("resize", onLayoutChange);
    if (rafId != null) cancelAnimationFrame(rafId);
    card.style.transform = "";
    card.style.transition = "";
  };
}
